import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, CircularProgress, Alert, Button, TablePagination, IconButton, Tooltip, Avatar } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import { RoomService } from '../../api/services/roomService';
import RoomTypeFormModal from '../../components/RoomTypeFormModal';
import AddIcon from '@mui/icons-material/Add';
import { useCurrency, formatCurrency } from '../../utils/currency';
import { extractTotalCount } from '../../components/common/tableStyles';

export default function RoomTypeSetup() {
    const { currency } = useCurrency();
    const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
    const tenantId = authData.tenantId || '1';
    const { tenants } = useSelector((state) => state.auth || { tenants: [] });
    const tenant = tenants.find(t => String(t.tenantId) === String(tenantId) || String(t.id) === String(tenantId) || String(t._id) === String(tenantId));
    const hotelStatus = tenant?.status || 'Active';
    const isGeometryLocked = hotelStatus === 'Active';

    const [roomTypes, setRoomTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingType, setEditingType] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [total, setTotal] = useState(0);

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const fetchRoomTypes = async () => {
        setLoading(true);
        try {
            const response = await RoomService.getRoomTypes({ page, limit: rowsPerPage });
            // Handle various backend response structures
            let typesArray = [];
            if (Array.isArray(response?.data?.data)) typesArray = response.data.data;
            else if (Array.isArray(response?.data?.items)) typesArray = response.data.items;
            else if (Array.isArray(response?.data)) typesArray = response.data;
            else if (Array.isArray(response?.items)) typesArray = response.items;
            else if (Array.isArray(response)) typesArray = response;
            
            setRoomTypes(typesArray);
            
            const meta = response?.meta || response?.pagination || response?.data?.meta || response?.data?.pagination || response?.data || response || {};
            setTotal(extractTotalCount(response, typesArray));
            setError(null);
        } catch (err) {
            console.error('Failed to fetch room types:', err);
            setError(err.message || 'Failed to load room types. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRoomTypes();
    }, [page, rowsPerPage]);

    const handleEdit = (type) => {
        setEditingType(type);
        setModalOpen(true);
    };

    const handleOpenModal = () => {
        setEditingType(null);
        setModalOpen(true);
    };

    const paginatedRoomTypes = roomTypes;

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" fontWeight="bold">Room Types Setup</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button 
                        variant="outlined"
                        startIcon={<RefreshIcon />} 
                        onClick={fetchRoomTypes} 
                        disabled={loading}
                    >
                        Refresh
                    </Button>
                    <Tooltip title={isGeometryLocked ? "Structural additions are locked while property is Active" : ""}>
                        <span>
                            <Button 
                                variant="contained" 
                                startIcon={<AddIcon />} 
                                onClick={handleOpenModal}
                                disabled={isGeometryLocked}
                            >
                                Add Room Type
                            </Button>
                        </span>
                    </Tooltip>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            <RoomTypeFormModal 
                open={modalOpen} 
                onClose={() => setModalOpen(false)} 
                roomType={editingType}
                onRefresh={fetchRoomTypes} 
            />

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Type Name</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Base Rate</TableCell>
                            <TableCell>Extra Bed Rate</TableCell>
                            <TableCell>Max Occupancy</TableCell>
                            <TableCell>Amenities</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                                    <CircularProgress size={30} />
                                    <Typography sx={{ mt: 1 }} color="text.secondary">Loading room types...</Typography>
                                </TableCell>
                            </TableRow>
                        ) : roomTypes.length > 0 ? (
                            paginatedRoomTypes.map((type) => (
                                <TableRow key={type.id} hover>
                                    <TableCell sx={{ fontWeight: 'medium' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            {type.images && type.images.length > 0 ? (
                                                <Avatar
                                                    src={type.images[0]}
                                                    variant="rounded"
                                                    sx={{ width: 44, height: 44, border: '1px solid #e2e8f0', objectFit: 'cover' }}
                                                />
                                            ) : (
                                                <Avatar
                                                    variant="rounded"
                                                    sx={{ width: 44, height: 44, bgcolor: '#e0e7ff', color: '#4338ca', fontSize: '0.85rem', fontWeight: 'bold' }}
                                                >
                                                    {type.name ? type.name.substring(0, 2).toUpperCase() : 'RT'}
                                                </Avatar>
                                            )}
                                            <Box>
                                                <Typography variant="body2" fontWeight="600">{type.name}</Typography>
                                                {type.images && type.images.length > 0 && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {type.images.length} {type.images.length === 1 ? 'photo' : 'photos'}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell>{type.description || 'No description provided'}</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                        {formatCurrency(type.baseRate, currency)}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'medium' }}>
                                        {formatCurrency(type.extraBedCharge !== undefined && type.extraBedCharge !== null ? type.extraBedCharge : (type.extraBedRate !== undefined && type.extraBedRate !== null ? type.extraBedRate : 0), currency)}
                                    </TableCell>
                                    <TableCell>
                                        {type.maxAdults} Adults, {type.maxKids} Kids
                                    </TableCell>
                                    <TableCell>
                                        {(() => {
                                            const rawAmenities = type.defaultAmenities || type.amenities || [];
                                            const mappedAmenities = rawAmenities.map(a => typeof a === 'string' ? a : (a?.name || '')).filter(Boolean);
                                            return (
                                                <>
                                                    {mappedAmenities.slice(0, 2).map((amenity, i) => (
                                                        <Chip key={i} label={amenity} size="small" variant="outlined" sx={{ mr: 0.5, mb: 0.5, fontSize: '0.7rem' }} />
                                                    ))}
                                                    {mappedAmenities.length > 2 && (
                                                        <Tooltip title={mappedAmenities.slice(2).join(', ')}>
                                                            <Chip label={`+${mappedAmenities.length - 2}`} size="small" variant="outlined" sx={{ mb: 0.5, fontSize: '0.7rem', cursor: 'pointer' }} />
                                                        </Tooltip>
                                                    )}
                                                </>
                                            );
                                        })()}
                                    </TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={type.status} 
                                            size="small" 
                                            color={type.status === 'Active' ? 'success' : 'default'} 
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title={isGeometryLocked ? "Structural edits are locked while property is Active" : "Edit Room Type"}>
                                            <span>
                                                <IconButton size="small" color="primary" onClick={() => handleEdit(type)} disabled={isGeometryLocked}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                                    <Typography color="text.secondary">No room types found.</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={roomTypes.length === 0 ? 0 : total}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[20, 50, 100]}
                />
            </TableContainer>
        </Box>
    );
}
